import { defineComponent as _defineComponent } from 'vue'
import InvoiceSection from '@/views/my-projects/components/invoice/InvoiceSection.vue';
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import { BcxFormField } from '@/models/BcxForm';
import Vue, {
  computed, onMounted, ref, watch
} from 'vue';
import { watchIgnorable } from '@vueuse/core';
import useResponsiveness from '@/mixins/useResponsiveness';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import { cloneDeep, isEqual } from 'lodash';
import { PayloadUpdateInvoice, ComponentInvoiceBasisData } from '@/models/Invoice';
import { Contract } from '@/models/Contracts';
import { convertToItemList } from '@/utils/selectItemTools';
import useUser from '@/mixins/useUser';
import useBeforeInput from '@/mixins/useBeforeInput';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import dayjs from 'dayjs';


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoiceBasisData',
  setup(__props) {

const {
  componentInvoiceBasisData,
  fetchUpdateInvoice,
  filteredContracts,
  fetchAllContracts,
  canInvoiceBeEdited,
  errorMessages,
  findChangedProps,
  createDebounceWithCallback,
  debounceFilterStatus
} = useInvoice();
const { validatePattern } = useBeforeInput();
const { getFormattedDate } = useFormattedDateTime();

const { tUserFallback: t } = useUser('self');
const { isMobileWidth } = useResponsiveness();

const horizontalGap = isMobileWidth.value ? '16px' : '40px';
const autoSaveTrigger = ref(0);

/**
 * Converts `dueDate` relative to `referenceDate`.
 * Numeric values are treated as day offsets and converted to a date.
 * Date strings are converted to the difference in days.
 * Empty values are returned unchanged.
 */
function dateValueOrDayDiff(value: string, referenceDate: string): string {
  if (value.trim() === '') return '';

  const refDay = dayjs(referenceDate);

  if (Number.isFinite(Number(value))) {
    return refDay.add(Number(value), 'day').format('YYYY-MM-DD');
  }

  const inputDay = dayjs(value);
  return inputDay.isValid() ? String(inputDay.diff(refDay, 'day')) : '';
}

/**
 *  Normalize dueDate to a day offset based on issueDate
 *  so the payment term stays consistent when issueDate changes.
 */
const componentInvoiceBasisDataEdited = computed(() => ({
  ...componentInvoiceBasisData.value,
  dueDate: dateValueOrDayDiff(componentInvoiceBasisData.value.dueDate, componentInvoiceBasisData.value.issueDate)
}));
const localBasisData = ref(cloneDeep(componentInvoiceBasisDataEdited.value));

const selectedContract = computed<Contract | null>(() => {
  const contract = filteredContracts.value.find((contract) => contract.bcReferenceId === localBasisData.value.associatedContract);
  return contract ?? null;
});

const contractFilteredBySameClient = computed(() => {
  const clientId = selectedContract.value?.client.user?.userId || selectedContract.value?.client.company?.rootUserId || '';
  return filteredContracts.value.filter((contract) => contract.client.user?.userId === clientId || contract.client.company?.rootUserId === clientId);
});

const fields = computed(() => {
  const invoiceType: BcxFormField = {
    name: 'invoiceType',
    label: `${t('invoice.basis-data.form.invoiceType_USERTYPE')}`,
    hint: '\u00A0', // The hint was added so that the distance down from each field is the same. This way, the distance with vertical-gap is always the same.
    type: 'plaintext',
    translatedPlaintext: `${t(`invoice.basis-data.invoiceTypeOptions.${localBasisData.value.invoiceType}_USERTYPE`)}`,
    disabled: !canInvoiceBeEdited.value,
  };

  const issueDate: BcxFormField = {
    name: 'issueDate',
    label: `${t('invoice.basis-data.form.issueDate_USERTYPE')}`,
    hint: '\u00A0',
    type: 'date',
    disabled: !canInvoiceBeEdited.value,
  };

  const invoiceNr: BcxFormField = {
    name: 'invoiceDocId',
    label: `${t('invoice.basis-data.form.invoiceDocId_USERTYPE')}`,
    type: 'text',
    placeholder: `${t('invoice.basis-data.form.invoiceNrPlaceholder_USERTYPE')}`,
    hint: '\u00A0',
    sonderlocke: 'primary-placeholder',
    textOptions: {
      maxLength: 24,
      hideCharsLeft: true
    },
    atBeforeInput: [
      validatePattern('\\p{L}0-9\\-\\/_\\.')
    ],
    disabled: !canInvoiceBeEdited.value,
  };

  const correctedInvoice: BcxFormField = {
    name: 'invoiceCorrectionDocId',
    label: `${t('invoice.basis-data.form.correctedInvoice_USERTYPE')}`,
    hint: '\u00A0',
    type: 'plaintext',
  };

  const billingPeriodStart: BcxFormField = {
    name: 'performancePeriodFrom',
    label: `${t('invoice.basis-data.form.performancePeriodFrom_USERTYPE')}`,
    hint: '\u00A0',
    type: 'date',
    dateOptions: {
      proposedDate: localBasisData.value.performancePeriodTo && !localBasisData.value.performancePeriodFrom ? localBasisData.value.performancePeriodTo : '',
      showX: !!localBasisData.value.performancePeriodFrom
    },
    disabled: !canInvoiceBeEdited.value,
  };

  const billingPeriodEnd: BcxFormField = {
    name: 'performancePeriodTo',
    label: `${t('invoice.basis-data.form.performancePeriodTo_USERTYPE')}`,
    hint: '\u00A0',
    type: 'date',
    dateOptions: {
      earliest: localBasisData.value.performancePeriodFrom,
    },
    showDisabledLabel: true,
    disabled: !canInvoiceBeEdited.value || !localBasisData.value.performancePeriodFrom
  };

  const dueDate: BcxFormField = {
    name: 'dueDate',
    label: `${t('invoice.basis-data.form.dueDate')}`,
    type: 'number',
    hint: `${getFormattedDate(dateValueOrDayDiff(localBasisData.value.dueDate, localBasisData.value.issueDate))}`,
    textOptions: {
      min: 1,
      max: 999,
      hideCharsLeft: true
    },
    disabled: !canInvoiceBeEdited.value,
  };

  // Felder-Array
  const fields: BcxFormField[] = isMobileWidth.value ? [
    { ...invoiceType, width: '50%' },
    { ...issueDate, width: '50%' },

    { ...invoiceNr, width: '50%' },
    ...(localBasisData.value.invoiceCorrectionDocId ? [{ ...correctedInvoice, width: '50%' }] : []),
    { ...dueDate, width: localBasisData.value.invoiceCorrectionDocId ? '100%' : '50%' },

    {
      ...billingPeriodStart,
      width: '50%',
      label: `${t('invoice.basis-data.form.billingPeriodStartMobile_USERTYPE')}`,
    },
    { ...billingPeriodEnd, width: '50%' },
  ] : [
    { ...invoiceType, width: '33%' },
    { ...issueDate, width: '34%' },
    { ...invoiceNr, width: '33%' },

    ...(localBasisData.value.invoiceCorrectionDocId ? [{ ...correctedInvoice, width: '100%' }] : []),

    { ...billingPeriodStart, width: '33%' },
    { ...billingPeriodEnd, width: '34%' },
    { ...dueDate, width: '33%' },
  ];
  return fields;
});

const referenceFields = computed(() => {
  const fields: BcxFormField[] = [
    {
      name: 'associatedContract',
      label: `${t('invoice.basis-data.form.associatedContract_USERTYPE')}`,
      hint: '\u00A0',
      width: isMobileWidth.value ? '50%' : '33%',
      type: 'select',
      options: convertToItemList(contractFilteredBySameClient.value, 'bcReferenceId', 'bcReferenceId'),
      disabled: !canInvoiceBeEdited.value,
    },
    {
      name: 'reference',
      label: `${t('invoice.basis-data.form.reference_USERTYPE')}`,
      width: isMobileWidth.value ? '100%' : '67%',
      type: 'text',
      textOptions: { maxLength: 60 },
      disabled: !canInvoiceBeEdited.value,
    },
  ];
  return fields;
});

function onBlur(field: BcxFormField) {
  if (selectedContract.value && field.name === 'associatedContract') {
    localBasisData.value.reference = selectedContract.value?.reference;
  }
}

// Payload should be updated with the local data
function createPayload(): PayloadUpdateInvoice {
  const baseData = localBasisData.value;

  return {
    baseData: {
      performancePeriodFrom: baseData.performancePeriodFrom,
      performancePeriodTo: baseData.performancePeriodTo,
      dueDate: dateValueOrDayDiff(baseData.dueDate, baseData.issueDate),
      contractId: selectedContract.value?.contractId || '',
      reference: baseData.reference,
      invoiceDocId: baseData.invoiceDocId,
      issueDate: baseData.issueDate
    },
  };
}

const { ignoreUpdates } = watchIgnorable<ComponentInvoiceBasisData>(
  localBasisData,
  async () => {
    try {
      if (!canInvoiceBeEdited.value) return;
      if (isEqual(componentInvoiceBasisDataEdited.value, localBasisData.value)) return;

      const payload = createPayload();
      await fetchUpdateInvoice(0, payload);
      ignoreUpdates(() => {
        componentInvoiceBasisData.value = cloneDeep({
          ...localBasisData.value,
          dueDate: dateValueOrDayDiff(localBasisData.value.dueDate, localBasisData.value.issueDate)
        });
      });
      autoSaveTrigger.value++;
    } finally {
      debounceFilterStatus.value.basisdata = false;
    }
  },
  {
    eventFilter: createDebounceWithCallback(1000, () => {
      debounceFilterStatus.value.basisdata = true;
    })
  }
);

watch(
  () => componentInvoiceBasisData.value,
  () => {
    ignoreUpdates(() => {
      localBasisData.value = cloneDeep(componentInvoiceBasisDataEdited.value);
    });
  },
  { immediate: false }
);

watch(
  () => localBasisData.value,
  (newValue: ComponentInvoiceBasisData, oldValue: ComponentInvoiceBasisData) => {
    const changed = findChangedProps(newValue, oldValue);
    const key = changed[0];

    if (changed.length === 1 && errorMessages.value[key]) {
      Vue.delete(errorMessages.value, key);
    }
  }
);

watch(
  () => [localBasisData.value.issueDate, localBasisData.value.dueDate],
  () => {
    if (!errorMessages.value.dueDate) return;
    if (!localBasisData.value.issueDate || !localBasisData.value.dueDate) {
      Vue.delete(errorMessages.value, 'dueDate');
      return;
    }

    const effectiveDueDate = dayjs(dateValueOrDayDiff(localBasisData.value.dueDate, localBasisData.value.issueDate)).startOf('day');
    if (effectiveDueDate.isValid() && !effectiveDueDate.isBefore(dayjs().startOf('day'))) {
      Vue.delete(errorMessages.value, 'dueDate');
    }
  }
);

watch(
  () => localBasisData.value.performancePeriodFrom,
  (newValue: string) => {
    if (!newValue) {
      localBasisData.value.performancePeriodTo = '';
      return;
    }

    const periodTo = localBasisData.value.performancePeriodTo;
    if (periodTo && new Date(newValue) > new Date(periodTo)) {
      localBasisData.value.performancePeriodTo = newValue;
    }
  }
);

onMounted(async () => {
  await fetchAllContracts();
});


return { __sfc: true,componentInvoiceBasisData, fetchUpdateInvoice, filteredContracts, fetchAllContracts, canInvoiceBeEdited, errorMessages, findChangedProps, createDebounceWithCallback, debounceFilterStatus, validatePattern, getFormattedDate, t, isMobileWidth, horizontalGap, autoSaveTrigger, dateValueOrDayDiff, componentInvoiceBasisDataEdited, localBasisData, selectedContract, contractFilteredBySameClient, fields, referenceFields, onBlur, createPayload, ignoreUpdates, InvoiceSection, BCXForm }
}

})